import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImprovBrief } from '@stage/contracts';
import { ApiError } from '../api/client.js';
import { useImprovClient } from './useImprovClient.js';
import type { Connectivity } from './useInteractionQueue.js';

/**
 * Poll the improv-brief list every 2s while visible and live (§20.11 step 13, §24.4). Mirrors
 * {@link useInteractionQueue}: pauses when the tab is hidden or the browser is offline, backs off on
 * repeated failures, and exposes an optimistic {@link BriefQueueState.applyLocal} so a performer edit
 * shows immediately between polls. `GET /improv-briefs` returns the full list (no cursor), so each
 * poll replaces the cache.
 */

const BASE_INTERVAL_MS = 2_000;
const BACKOFF_MS = [5_000, 10_000, 30_000] as const;

export interface BriefQueueState {
  briefs: ImprovBrief[];
  connectivity: Connectivity;
  lastUpdatedAt: number | null;
  error: ApiError | null;
  refresh: () => void;
  applyLocal: (brief: ImprovBrief) => void;
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export function useBriefQueue(gigId: string, active: boolean): BriefQueueState {
  const client = useImprovClient();
  const [briefs, setBriefs] = useState<ImprovBrief[]>([]);
  const [connectivity, setConnectivity] = useState<Connectivity>('online');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const byIdRef = useRef<Map<string, ImprovBrief>>(new Map());
  const failuresRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  const publish = useCallback(() => {
    setBriefs([...byIdRef.current.values()]);
  }, []);

  const applyLocal = useCallback(
    (brief: ImprovBrief) => {
      byIdRef.current.set(brief.briefId, brief);
      publish();
    },
    [publish],
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    if (!activeRef.current || !isVisible() || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) setConnectivity('offline');
      return;
    }
    try {
      const items = await client.listBriefs(gigId);
      if (cancelledRef.current) return;
      const next = new Map<string, ImprovBrief>();
      for (const b of items) next.set(b.briefId, b);
      byIdRef.current = next;
      publish();
      failuresRef.current = 0;
      setError(null);
      setConnectivity('online');
      setLastUpdatedAt(Date.now());
    } catch (err) {
      if (cancelledRef.current) return;
      failuresRef.current += 1;
      setConnectivity('reconnecting');
      if (err instanceof ApiError) setError(err);
    } finally {
      if (!cancelledRef.current) {
        clearTimer();
        const f = failuresRef.current;
        const delay =
          f === 0 ? BASE_INTERVAL_MS : BACKOFF_MS[Math.min(f - 1, BACKOFF_MS.length - 1)]!;
        timerRef.current = setTimeout(() => void poll(), delay);
      }
    }
  }, [client, gigId, publish, clearTimer]);

  const refresh = useCallback(() => {
    failuresRef.current = 0;
    clearTimer();
    void poll();
  }, [clearTimer, poll]);

  // Reset the cache when the gig changes.
  useEffect(() => {
    byIdRef.current = new Map();
    failuresRef.current = 0;
    setBriefs([]);
    setLastUpdatedAt(null);
  }, [gigId]);

  useEffect(() => {
    cancelledRef.current = false;
    if (active) void poll();
    else clearTimer();

    const onVisibility = () => {
      if (isVisible() && activeRef.current) refresh();
      else clearTimer();
    };
    const onOnline = () => {
      setConnectivity('online');
      if (activeRef.current) refresh();
    };
    const onOffline = () => {
      setConnectivity('offline');
      clearTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      cancelledRef.current = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [gigId, active]);

  return { briefs, connectivity, lastUpdatedAt, error, refresh, applyLocal };
}
